import * as THREE from "three";
import type { DungeonMap, GridCell } from "../types/grid.js";
import { getSurfaceMaterial } from "./MaterialCache.js";

// ── Constants ────────────────────────────────────────────────────────────────

const CELL_SIZE = 1; // world units per grid cell — must match CameraController
const WALL_HEIGHT = 1; // world units from floor to ceiling

// ── Materials ────────────────────────────────────────────────────────────────
// PBR materials (W20) — react to ambient + point lights added in main.tsx
// and pick up authored normal / roughness maps when an adventure ships them.
//
// Outdoor cells (GridCell.noCeiling === true) route floor and walls to
// separate material variants so adventures can ship their own
// dirt/grass/tree-bark texture set without overwriting the dungeon stone.
// The fallback colors below are the visual floor before textures land.
//
// Materials are resolved through `MaterialCache` (T01) so each active
// adventure gets its own set; see `MaterialCache.ts` for the bug that
// motivated the cache.

const FLOOR_OPTIONS = { surface: "floor", color: 0x3a3a3a } as const;
const CEILING_OPTIONS = { surface: "ceiling", color: 0x1a1a1a } as const;
const WALL_OPTIONS = {
  surface: "wall",
  color: 0x888888,
  side: THREE.FrontSide,
} as const;
const FLOOR_OUTDOOR_OPTIONS = {
  surface: "floor_outdoor",
  color: 0x4a6a3a, // grass/dirt — shows through until a texture lands
} as const;
const WALL_OUTDOOR_OPTIONS = {
  surface: "wall_outdoor",
  color: 0x2e4a25, // deep forest green (tree line)
  side: THREE.FrontSide,
} as const;

// ── Geometry factory ─────────────────────────────────────────────────────────

/** Shared source geometries — cloned per InstancedMesh to avoid WebGL conflicts. */
function makePlane(): THREE.PlaneGeometry {
  return new THREE.PlaneGeometry(CELL_SIZE, WALL_HEIGHT);
}

// ── Matrix helpers ───────────────────────────────────────────────────────────

const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scale = new THREE.Vector3(1, 1, 1);

function matrix(px: number, py: number, pz: number, ex: number, ey: number, ez: number): THREE.Matrix4 {
  _pos.set(px, py, pz);
  _quat.setFromEuler(new THREE.Euler(ex, ey, ez));
  return new THREE.Matrix4().compose(_pos, _quat, _scale);
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Translates a DungeonMap into Three.js geometry using the razor-wall model.
 *
 * Produces up to five InstancedMeshes added to `scene`:
 *   - floors            (dungeon stone floor)
 *   - floors_outdoor    (grass / dirt, for cells marked noCeiling)
 *   - ceilings          (only for non-outdoor cells)
 *   - walls             (dungeon stone walls, for edges between two indoor cells or edge-of-map)
 *   - walls_outdoor     (tree-line / fence walls, when either side is outdoor)
 *
 * The wall normal always faces inward (toward the passable cell), enabling
 * single-sided rendering without visible gaps. A wall uses the outdoor
 * material when the cell on *either* side of the razor is outdoor — this
 * avoids a jarring stone/tree mix at the boundary.
 */
export function buildDungeon(scene: THREE.Scene, map: DungeonMap): void {
  // T03: group matrices by (surface, setKey). Keys are the *effective* set
  // for a cell (cell.textureSet ?? map.textureSet ?? undefined). Walls are
  // credited to the owning non-solid cell so their texture set matches the
  // room they face.
  type BySet = Map<string | undefined, THREE.Matrix4[]>;
  const floors: BySet = new Map();
  const floorsOutdoor: BySet = new Map();
  const ceilings: BySet = new Map();
  const walls: BySet = new Map();
  const wallsOutdoor: BySet = new Map();

  const pushTo = (
    bucket: BySet,
    setKey: string | undefined,
    m: THREE.Matrix4,
  ): void => {
    let arr = bucket.get(setKey);
    if (!arr) {
      arr = [];
      bucket.set(setKey, arr);
    }
    arr.push(m);
  };

  const cellAt = (x: number, z: number): GridCell | undefined => {
    if (x < 0 || x >= map.width || z < 0 || z >= map.height) return undefined;
    return map.cells[z * map.width + x];
  };

  /** An edge is "outdoor" when the walkable cell or its solid neighbour is outdoor. */
  const edgeIsOutdoor = (self: GridCell, nx: number, nz: number): boolean => {
    if (self.noCeiling === true) return true;
    const neighbour = cellAt(nx, nz);
    return neighbour?.noCeiling === true;
  };

  const effectiveSet = (cell: GridCell): string | undefined =>
    cell.textureSet ?? map.textureSet;

  for (const cell of map.cells) {
    if (cell.solid) continue;

    const x = cell.position.x * CELL_SIZE;
    const z = cell.position.z * CELL_SIZE;
    const setKey = effectiveSet(cell);

    const floorMat = matrix(x, 0, z, -Math.PI / 2, 0, 0);
    pushTo(cell.noCeiling === true ? floorsOutdoor : floors, setKey, floorMat);

    if (cell.noCeiling !== true) {
      pushTo(ceilings, setKey, matrix(x, WALL_HEIGHT, z, Math.PI / 2, 0, 0));
    }

    const pushWall = (m: THREE.Matrix4, outdoor: boolean): void => {
      pushTo(outdoor ? wallsOutdoor : walls, setKey, m);
    };

    if (cell.edges.north) {
      pushWall(
        matrix(x, WALL_HEIGHT / 2, z - CELL_SIZE / 2, 0, 0, 0),
        edgeIsOutdoor(cell, cell.position.x, cell.position.z - 1),
      );
    }
    if (cell.edges.south) {
      pushWall(
        matrix(x, WALL_HEIGHT / 2, z + CELL_SIZE / 2, 0, Math.PI, 0),
        edgeIsOutdoor(cell, cell.position.x, cell.position.z + 1),
      );
    }
    if (cell.edges.east) {
      pushWall(
        matrix(x + CELL_SIZE / 2, WALL_HEIGHT / 2, z, 0, -Math.PI / 2, 0),
        edgeIsOutdoor(cell, cell.position.x + 1, cell.position.z),
      );
    }
    if (cell.edges.west) {
      pushWall(
        matrix(x - CELL_SIZE / 2, WALL_HEIGHT / 2, z, 0, Math.PI / 2, 0),
        edgeIsOutdoor(cell, cell.position.x - 1, cell.position.z),
      );
    }
  }

  const emit = (
    bucket: BySet,
    baseOptions: { readonly surface: string; readonly color: number; readonly side?: THREE.Side },
  ): void => {
    for (const [setKey, mats] of bucket) {
      if (mats.length === 0) continue;
      const opts = setKey ? { ...baseOptions, setKey } : baseOptions;
      scene.add(makeInstancedMesh(makePlane(), getSurfaceMaterial(opts), mats));
    }
  };

  emit(floors, FLOOR_OPTIONS);
  emit(floorsOutdoor, FLOOR_OUTDOOR_OPTIONS);
  emit(ceilings, CEILING_OPTIONS);
  emit(walls, WALL_OPTIONS);
  emit(wallsOutdoor, WALL_OUTDOOR_OPTIONS);
}

// ── Internal ─────────────────────────────────────────────────────────────────

function makeInstancedMesh(
  geo: THREE.PlaneGeometry,
  mat: THREE.Material,
  matrices: THREE.Matrix4[],
): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geo, mat, matrices.length);
  for (let i = 0; i < matrices.length; i++) {
    mesh.setMatrixAt(i, matrices[i]!);
  }
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}
